import { useMemo, useState } from 'react'
import {
  AlertTriangle,
  Bot,
  Plus,
  FileStack,
  PlayCircle,
  ShieldCheck,
  Sparkles,
  Lightbulb,
  ShieldAlert,
  BriefcaseBusiness,
} from 'lucide-react'
import { caseWorkspaceService } from '@/features/case-workspace/service'
import { deriveCbp7501LineAutomation } from '@/features/case-workspace/cbp7501LineAutomation'
import {
  filterVisibleReviewState,
  filterVisibleStructuredDocuments,
} from '@/features/case-workspace/mvpVisibility'
import { DOCUMENT_DEFINITIONS } from '@/features/case-workspace/schemas'
import type {
  CaseReviewState,
  DocumentWorkspaceFocusTarget,
  StructuredDocumentRecord,
  SupportedDocumentType,
} from '@/features/case-workspace/types'
import type { CaseEvent } from '@/lib/cases'
import {
  StatusPill,
  TrackerWorkspacePanel,
  reviewReadinessLabel,
  findingSeverityLabel,
  toneForSeverity,
} from '@/features/case-workspace/WorkspaceShared'

type CorrectiveActionItem = {
  id: string
  title: string
  summary: string
  detail: string
  severity: 'informational' | 'low' | 'moderate' | 'high' | 'blocks_acceptance'
  status: 'open' | 'investigating' | 'resolved'
  documentId?: string
  fieldKey?: string
  sourceEvidence?: string
  linkedAction?: string
  relatedFields: string[]
  currentValue?: string
  recommendedValue?: string
  recommendedActions: string[]
  quickFix?: {
    documentId: string
    recommendationId: string
  }
}

type CorrectiveActionTab = {
  id: string
  label: string
  badge: number
}

type AssistantWorkspaceSection = {
  id: string
  title: string
  icon: JSX.Element
  items: string[]
  emptyText: string
}

// eslint-disable-next-line react-refresh/only-export-components
export function countCorrectiveActionItems(
  findings: CaseReviewState['findings'],
  structuredDocuments: StructuredDocumentRecord[],
) {
  return buildCorrectiveActionItems(findings, structuredDocuments).length
}

export function ReviewWorkspaceTab({
  actorId,
  actorLabel,
  structuredDocuments,
  reviewState,
  onReviewStateChange,
  onActivityEvent,
  onOpenFindingTarget,
  onApplyQuickFix,
}: {
  actorId?: string
  actorLabel: string
  structuredDocuments: StructuredDocumentRecord[]
  reviewState: CaseReviewState
  onReviewStateChange: (next: CaseReviewState) => void
  onActivityEvent: (event: CaseEvent) => void
  onOpenFindingTarget: (target: DocumentWorkspaceFocusTarget) => void
  onApplyQuickFix: (target: { documentId: string; recommendationId: string }) => Promise<void> | void
}) {
  const visibleStructuredDocuments = filterVisibleStructuredDocuments(structuredDocuments)
  const visibleReviewState = filterVisibleReviewState(reviewState, visibleStructuredDocuments)
  const findings = useMemo(
    () => (Array.isArray(visibleReviewState.findings) ? visibleReviewState.findings : []),
    [visibleReviewState.findings],
  )
  const actionItems = useMemo(
    () => buildCorrectiveActionItems(findings, visibleStructuredDocuments),
    [findings, visibleStructuredDocuments],
  )
  const assistantWorkspaceSections = useMemo(
    () => buildAssistantWorkspaceSections(visibleReviewState, visibleStructuredDocuments, actionItems),
    [actionItems, visibleReviewState, visibleStructuredDocuments],
  )
  const correctiveActionTabs = useMemo(
    () => buildCorrectiveActionTabs(actionItems, visibleStructuredDocuments),
    [actionItems, visibleStructuredDocuments],
  )
  const [activeDocumentTabId, setActiveDocumentTabId] = useState<string>(correctiveActionTabs[0]?.id ?? 'all')
  const [selectedActionId, setSelectedActionId] = useState<string>(actionItems[0]?.id ?? '')
  const [busy, setBusy] = useState(false)
  const [quickFixBusyId, setQuickFixBusyId] = useState<string | null>(null)
  const resolvedActiveDocumentTabId = correctiveActionTabs.some((tab) => tab.id === activeDocumentTabId)
    ? activeDocumentTabId
    : correctiveActionTabs[0]?.id ?? 'all'
  const filteredActionItems = useMemo(
    () =>
      resolvedActiveDocumentTabId === 'all'
        ? actionItems
        : actionItems.filter((item) => item.documentId === resolvedActiveDocumentTabId),
    [actionItems, resolvedActiveDocumentTabId],
  )
  const resolvedSelectedActionId = filteredActionItems.some((item) => item.id === selectedActionId)
    ? selectedActionId
    : filteredActionItems[0]?.id ?? ''

  async function initiateReview() {
    if (!visibleReviewState.isReadyForReview) return
    setBusy(true)
    const { reviewState: nextState, event } = await caseWorkspaceService.initiateAwaReview({
      reviewState: visibleReviewState,
      actorId,
      actorLabel,
    })
    onReviewStateChange(nextState)
    onActivityEvent(event)
    setBusy(false)
  }

  function handleSelectAction(actionId: string) {
    setSelectedActionId((current) => (current === actionId ? '' : actionId))
  }

  async function handleApplyQuickFix(item: CorrectiveActionItem) {
    if (!item.quickFix) return
    setQuickFixBusyId(item.id)
    try {
      await onApplyQuickFix(item.quickFix)
    } finally {
      setQuickFixBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <ReviewWorkspacePanel
        reviewState={visibleReviewState}
        actionItemsCount={actionItems.length}
        structuredDocuments={visibleStructuredDocuments}
        onInitiateReview={initiateReview}
        busy={busy}
      />

      <AssistantWorkspacePanel sections={assistantWorkspaceSections} />

      <CorrectiveActionsRequiredPanel
        tabs={correctiveActionTabs}
        activeTabId={resolvedActiveDocumentTabId}
        onTabChange={setActiveDocumentTabId}
        actionItems={filteredActionItems}
        selectedActionId={resolvedSelectedActionId}
        onSelect={handleSelectAction}
        onOpenFindingTarget={onOpenFindingTarget}
        onApplyQuickFix={handleApplyQuickFix}
        quickFixBusyId={quickFixBusyId}
      />
    </div>
  )
}

function AssistantWorkspacePanel({
  sections,
}: {
  sections: AssistantWorkspaceSection[]
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm">
      <div className="border-b border-neutral-200 px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-full bg-secondary-lightest text-primary">
            <Bot size={18} aria-hidden="true" />
          </div>
          <div>
            <h4 className="text-base font-bold text-primary">AWA Assistant workspace</h4>
            <p className="mt-1 text-sm text-neutral-600">
              Recommendations, process flags, and industry considerations that the agent should keep in view for this case before resolving individual corrective actions.
            </p>
          </div>
        </div>
      </div>
      <div className="grid gap-4 px-5 py-4 xl:grid-cols-3">
        {sections.map((section) => (
          <div key={section.id} className="rounded-xl border border-neutral-200 bg-neutral-50/70 p-4">
            <div className="flex items-center gap-2 text-primary">
              {section.icon}
              <h5 className="text-sm font-bold">{section.title}</h5>
            </div>
            {section.items.length > 0 ? (
              <ul className="mt-3 space-y-2 text-sm text-neutral-700">
                {section.items.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="mt-[0.4rem] h-1.5 w-1.5 flex-none rounded-full bg-secondary" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-neutral-500">{section.emptyText}</p>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

function ReviewWorkspacePanel({
  reviewState,
  actionItemsCount,
  structuredDocuments,
  onInitiateReview,
  busy,
}: {
  reviewState: CaseReviewState
  actionItemsCount: number
  structuredDocuments: StructuredDocumentRecord[]
  onInitiateReview: () => Promise<void>
  busy: boolean
}) {
  const readyCount = reviewState.documentSummaries.filter(
    (summary) => summary.completenessStatus === 'complete' || summary.completenessStatus === 'reviewed',
  ).length
  const summaryCopy: Record<CaseReviewState['readinessStatus'], string> = {
    missing_required_documents: 'Required documents are still missing or incomplete. Finish the case record before starting review.',
    incomplete_documents: 'The case is partly prepared, but some documents still need follow up before review can begin cleanly.',
    ready_with_warnings: 'The case can move into review, but a few warnings should be checked carefully.',
    ready_for_review: 'The case record is ready for review and AWA can be started.',
    review_in_progress: 'AWA is currently reviewing the case and surfacing issues that may need attention.',
    review_completed: 'AWA review is complete. Resolve any remaining findings and confirm the case record.',
  }
  const canActivateReview =
    reviewState.awaStatus === 'not_started'
      ? reviewState.isReadyForReview
      : reviewState.isReadyForReview && actionItemsCount === 0
  const primaryActionLabel =
    reviewState.awaStatus === 'in_progress'
      ? 'AWA review in progress'
      : reviewState.awaStatus === 'completed'
        ? actionItemsCount === 0
          ? 'Activate AWA Review'
          : 'Resolve corrective actions first'
        : 'Initiate AWA Review'

  return (
    <TrackerWorkspacePanel
      eyebrow="Review workspace"
      title="AWA review workspace"
      helpText={summaryCopy[reviewState.readinessStatus]}
      action={
        <>
          <button
            type="button"
            onClick={() => void onInitiateReview()}
            disabled={!canActivateReview || busy || reviewState.awaStatus === 'in_progress'}
            className="inline-flex items-center gap-2 rounded bg-accent-sunset px-4 py-2 text-sm font-semibold text-white hover:bg-accent-coral disabled:cursor-not-allowed disabled:bg-neutral-300"
          >
            <PlayCircle size={16} aria-hidden="true" />
            {busy ? 'Starting review…' : primaryActionLabel}
          </button>
        </>
      }
      summaryTiles={[
        {
          label: 'Case readiness',
          value: reviewReadinessLabel(reviewState.readinessStatus),
          icon: <ShieldCheck size={18} aria-hidden="true" />,
        },
        {
          label: 'Documents ready',
          value: `${readyCount}/${structuredDocuments.length}`,
          icon: <FileStack size={18} aria-hidden="true" />,
        },
        {
          label: 'Open flags',
          value: `${actionItemsCount}`,
          icon: <AlertTriangle size={18} aria-hidden="true" />,
        },
        {
          label: 'AWA Findings',
          value: `${reviewState.riskSummary.total}`,
          icon: <Sparkles size={18} aria-hidden="true" />,
        },
      ]}
    />
  )
}

function CorrectiveActionsRequiredPanel({
  tabs,
  activeTabId,
  onTabChange,
  actionItems,
  selectedActionId,
  onSelect,
  onOpenFindingTarget,
  onApplyQuickFix,
  quickFixBusyId,
}: {
  tabs: CorrectiveActionTab[]
  activeTabId: string
  onTabChange: (tabId: string) => void
  actionItems: CorrectiveActionItem[]
  selectedActionId: string
  onSelect: (actionId: string) => void
  onOpenFindingTarget: (target: DocumentWorkspaceFocusTarget) => void
  onApplyQuickFix: (item: CorrectiveActionItem) => Promise<void>
  quickFixBusyId: string | null
}) {
  const [showDocumentSupportNote, setShowDocumentSupportNote] = useState(false)

  return (
    <section className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm">
      <div className="border-b border-neutral-200 px-5 py-4">
        <div>
          <h4 className="text-base font-bold text-primary">Corrective actions required</h4>
          <p className="mt-1 text-sm text-neutral-600">
            AWA findings and document validation issues appear here when the review detects risks, discrepancies, or missing information that should be resolved before the case moves forward.
          </p>
        </div>
        <div role="tablist" aria-label="Corrective action documents" className="mt-4 flex flex-wrap gap-1 rounded-lg border border-neutral-200 bg-white p-1 shadow-sm">
          {tabs.map((tab) => {
            const isActive = tab.id === activeTabId
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                aria-controls={`corrective-actions-panel-${tab.id}`}
                id={`corrective-actions-tab-${tab.id}`}
                type="button"
                onClick={() => onTabChange(tab.id)}
                className={`rounded-md px-3 py-2 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2 ${
                  isActive
                    ? 'bg-secondary-lightest text-primary'
                    : 'text-neutral-500 hover:bg-neutral-50 hover:text-primary'
                }`}
              >
                {tab.label}
                <span
                  className={`ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
                    isActive ? 'bg-primary text-white' : 'bg-neutral-100 text-neutral-600'
                  }`}
                >
                  {tab.badge}
                </span>
              </button>
            )
          })}
          <div className="relative">
            <button
              type="button"
              aria-label="More document types"
              aria-expanded={showDocumentSupportNote}
              onClick={() => setShowDocumentSupportNote((current) => !current)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#0b1460] text-white shadow-sm transition hover:bg-[#13207f] focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
            >
              <Plus size={22} strokeWidth={3.25} aria-hidden="true" />
            </button>
            {showDocumentSupportNote ? (
              <div className="absolute right-0 top-[calc(100%+0.5rem)] z-10 w-64 rounded-lg border border-neutral-200 bg-white p-3 text-sm text-neutral-600 shadow-lg">
                This version of VICE only supports the CBP Form 7501 in the corrective actions workspace.
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div
        id={`corrective-actions-panel-${activeTabId}`}
        role="tabpanel"
        aria-labelledby={`corrective-actions-tab-${activeTabId}`}
        className="min-h-[32rem] bg-neutral-100 p-4"
      >
        <div className="rounded-lg border border-neutral-200 bg-white shadow-sm">
          {actionItems.length === 0 ? (
            <div className="p-4 text-sm text-neutral-600">
              No corrective actions are available yet. Start review or complete the required document fields to populate this workspace.
            </div>
          ) : (
            <ul className="divide-y divide-neutral-200">
              {actionItems.map((item) => {
                const isOpen = selectedActionId === item.id

                return (
                  <li key={item.id}>
                    <div
                      className={`px-4 py-3 ${
                        isOpen
                          ? 'border-l-4 border-secondary bg-secondary-lightest'
                          : 'hover:bg-neutral-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <button type="button" onClick={() => onSelect(item.id)} className="min-w-0 flex-1 text-left">
                          <div className="text-sm font-semibold text-primary">{item.title}</div>
                          <p className="mt-1 text-xs text-neutral-500">{item.summary}</p>
                        </button>
                        <div className="flex flex-col items-end gap-2">
                          <StatusPill label={findingSeverityLabel(item.severity)} tone={toneForSeverity(item.severity)} />
                        </div>
                      </div>
                      {isOpen && (
                        <div className="mt-4">
                          <IssueDetailPanel
                            item={item}
                            onOpenFindingTarget={onOpenFindingTarget}
                            onApplyQuickFix={() => onApplyQuickFix(item)}
                            quickFixBusy={quickFixBusyId === item.id}
                          />
                        </div>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  )
}

function IssueDetailPanel({
  item,
  onOpenFindingTarget,
  onApplyQuickFix,
  quickFixBusy,
}: {
  item: CorrectiveActionItem | undefined
  onOpenFindingTarget: (target: DocumentWorkspaceFocusTarget) => void
  onApplyQuickFix: () => Promise<void>
  quickFixBusy: boolean
}) {
  if (!item) {
    return null
  }

  return (
    <section className="space-y-3">
      <div className="rounded-lg bg-primary p-4 text-white shadow-sm">
        <div className="text-xs font-semibold uppercase tracking-wider text-white/75">Recommended Action(s)</div>
        <div className="mt-2 space-y-3 text-sm text-white">
          <ul className="space-y-1.5">
            {Array.from(new Set(item.recommendedActions)).map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ul>
          {item.linkedAction && !item.recommendedActions.includes(item.linkedAction) ? <p>{item.linkedAction}</p> : null}
          {item.quickFix ? (
            <button
              type="button"
              onClick={() => void onApplyQuickFix()}
              disabled={quickFixBusy}
              className="rounded bg-white px-3 py-2 text-sm font-semibold text-primary hover:bg-neutral-100 disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:text-neutral-600"
            >
              {quickFixBusy ? 'Applying fix…' : 'Apply suggested fix'}
            </button>
          ) : null}
        </div>
      </div>
      {item.documentId && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() =>
              onOpenFindingTarget({
                documentId: item.documentId,
                fieldKey: item.fieldKey,
                findingId: item.id,
              })
            }
            className="rounded border border-secondary px-3 py-2 text-sm font-semibold text-secondary hover:bg-secondary-lightest"
          >
            Open in Documents
          </button>
        </div>
      )}
    </section>
  )
}

function buildCorrectiveActionItems(
  findings: CaseReviewState['findings'],
  structuredDocuments: StructuredDocumentRecord[],
): CorrectiveActionItem[] {
  const itemsFromFindings: CorrectiveActionItem[] = findings.flatMap((finding) => {
    const record = finding.documentId
      ? structuredDocuments.find((item) => item.id === finding.documentId)
      : structuredDocuments.find((item) => item.documentType === finding.documentType)
    const fieldIssues = finding.fieldKey
      ? record?.validationIssues.filter((issue) => issue.fieldKey === finding.fieldKey) ?? []
      : []
    const hasOpenFieldIssue = fieldIssues.length > 0
    const isFieldBasedFindingResolved = Boolean(finding.fieldKey) && !hasOpenFieldIssue
    if (finding.status === 'resolved' || isFieldBasedFindingResolved) {
      return []
    }
    const recommendation = finding.fieldKey
      ? record?.awaRecommendations.find(
        (item) =>
          item.findingId === finding.id
          && item.fieldKey === finding.fieldKey
          && Boolean(item.recommendedValue)
          && item.state !== 'resolved',
      )
      : undefined

    return [{
      id: finding.id,
      title: finding.title,
      summary: finding.summary,
      detail: finding.detail,
      severity: finding.severity,
      status: finding.status,
      documentId: record?.id ?? finding.documentId ?? undefined,
      fieldKey: finding.fieldKey,
      sourceEvidence: finding.sourceEvidence,
      linkedAction: finding.linkedAction,
      relatedFields: finding.relatedFields,
      currentValue: finding.fieldKey && record ? record.fields[finding.fieldKey]?.displayValue || record.fields[finding.fieldKey]?.value || '' : undefined,
      recommendedValue: recommendation?.recommendedValue,
      recommendedActions: [finding.summary, finding.linkedAction].filter(Boolean) as string[],
      quickFix: recommendation && (record?.id ?? finding.documentId)
        ? {
            documentId: record?.id ?? finding.documentId ?? '',
            recommendationId: recommendation.id,
          }
        : undefined,
    }]
  })

  const representedFieldTargets = new Set(
    itemsFromFindings
      .filter((item) => item.documentId && item.fieldKey)
      .map((item) => `${item.documentId}:${item.fieldKey}`),
  )

  const itemsFromValidation = structuredDocuments.flatMap((record) => {
    const groupedIssues = new Map<string, typeof record.validationIssues>()

    for (const issue of record.validationIssues.filter((candidate) => !representedFieldTargets.has(`${record.id}:${candidate.fieldKey}`))) {
      const existing = groupedIssues.get(issue.fieldKey) ?? []
      existing.push(issue)
      groupedIssues.set(issue.fieldKey, existing)
    }

    return Array.from(groupedIssues.entries()).map(([fieldKey, issues]) => {
      const fieldDefinition = findFieldDefinition(record.documentType, fieldKey)
      const recommendation = record.awaRecommendations.find(
        (item) => item.fieldKey === fieldKey && Boolean(item.recommendedValue) && item.state !== 'resolved',
      )
      const currentValue = currentValueForIssue(record, fieldKey)
      const lineItemLabel = lineItemLabelForIssue(record, fieldKey)
      const severity = issues.some((issue) => issue.severity === 'error') ? 'high' : 'moderate'
      const uniqueMessages = Array.from(new Set(issues.map((issue) => issue.message)))

      return {
        id: `${record.id}-validation-${fieldKey}`,
        title: `${lineItemLabel ? `${lineItemLabel} · ` : ''}${fieldDefinition?.label ?? baseFieldKey(fieldKey)} needs attention`,
        summary: uniqueMessages[0] ?? 'This field needs attention.',
        detail: `${record.title} has validation outcomes that should be resolved before the case moves forward.`,
        severity,
        status: 'open',
        documentId: record.id,
        fieldKey,
        relatedFields: [fieldKey],
        currentValue,
        recommendedValue: recommendation?.recommendedValue,
        recommendedActions: uniqueMessages,
        quickFix: recommendation
          ? {
              documentId: record.id,
              recommendationId: recommendation.id,
            }
          : undefined,
      } satisfies CorrectiveActionItem
    })
  })

  return [...itemsFromFindings, ...itemsFromValidation].sort(
    (left, right) => severityRank(right.severity) - severityRank(left.severity),
  )
}

function buildAssistantWorkspaceSections(
  reviewState: CaseReviewState,
  structuredDocuments: StructuredDocumentRecord[],
  actionItems: CorrectiveActionItem[],
): AssistantWorkspaceSection[] {
  const recommendationItems = uniqueStrings(
    structuredDocuments.flatMap((record) =>
      record.awaRecommendations
        .filter((recommendation) => recommendation.state !== 'resolved')
        .map((recommendation) =>
          recommendation.recommendedValue
            ? `${recommendation.message} Recommended value: ${recommendation.recommendedValue}.`
            : recommendation.message,
        ),
    ),
  ).slice(0, 5)

  const processFlagItems = uniqueStrings(
    actionItems
      .filter((item) => item.status !== 'resolved')
      .map((item) => `${findingSeverityLabel(item.severity)}: ${item.summary}`),
  ).slice(0, 6)

  const industryConsiderationItems = uniqueStrings(
    structuredDocuments.flatMap((record) =>
      record.documentType === 'cbp_7501'
        ? record.lineItems.flatMap((lineItem) => {
            const automation = deriveCbp7501LineAutomation(lineItem)
            const linePrefix = lineItem.lineNumber ? `Line ${lineItem.lineNumber}: ` : ''
            const items = automation.flags.map((flag) => `${linePrefix}${industryConsiderationLabel(flag)}`)
            if (automation.quotaOrVisaMessage) {
              items.push(`${linePrefix}${automation.quotaOrVisaMessage}.`)
            }
            return items
          })
        : [],
    ),
  ).slice(0, 6)

  return [
    {
      id: 'recommendations',
      title: 'Recommendations',
      icon: <Lightbulb size={16} aria-hidden="true" />,
      items: recommendationItems,
      emptyText:
        reviewState.awaStatus === 'not_started'
          ? 'AWA recommendations will appear here after review starts.'
          : 'No active AWA recommendations are open for this case right now.',
    },
    {
      id: 'process-flags',
      title: 'Process flags',
      icon: <ShieldAlert size={16} aria-hidden="true" />,
      items: processFlagItems,
      emptyText: 'No active process flags are currently open for this case.',
    },
    {
      id: 'industry-considerations',
      title: 'Industry considerations',
      icon: <BriefcaseBusiness size={16} aria-hidden="true" />,
      items: industryConsiderationItems,
      emptyText: 'No additional HTS-driven industry considerations are currently surfaced for this case.',
    },
  ]
}

function industryConsiderationLabel(flag: string) {
  switch (flag) {
    case 'SECTION_232_STEEL':
      return 'Section 232 steel reporting applies. Complete the steel-related country reporting fields.'
    case 'SECTION_232_ALUMINUM':
      return 'Section 232 aluminum reporting applies. Complete the smelt and cast country reporting fields.'
    case 'AD_CVD_POSSIBLE':
      return 'AD/CVD review may apply for this HTSUS number.'
    case 'QUOTA_OR_VISA':
      return 'Quota or visa review may be required for this line item.'
    case 'IRC_TAX_APPLICABLE':
      return 'Internal revenue tax review may be required for this line item.'
    case 'SPECIAL_PROGRAM_INDICATOR_ALLOWED':
      return 'A special program indicator may be allowed for this HTSUS number.'
    default:
      return flag.replace(/_/g, ' ')
  }
}

function uniqueStrings(items: string[]) {
  return [...new Set(items.filter(Boolean))]
}

function buildCorrectiveActionTabs(
  actionItems: CorrectiveActionItem[],
  structuredDocuments: StructuredDocumentRecord[],
): CorrectiveActionTab[] {
  const tabs: CorrectiveActionTab[] = [
    {
      id: 'all',
      label: 'All',
      badge: actionItems.length,
    },
  ]

  for (const record of structuredDocuments) {
    const count = actionItems.filter((item) => item.documentId === record.id).length
    tabs.push({
      id: record.id,
      label: record.title,
      badge: count,
    })
  }

  return tabs
}

function findFieldDefinition(documentType: SupportedDocumentType, fieldKey: string) {
  const normalizedKey = baseFieldKey(fieldKey)
  for (const section of DOCUMENT_DEFINITIONS[documentType].sections) {
    const field = section.fields.find((item) => item.key === normalizedKey)
    if (field) return field
  }
  return undefined
}

function baseFieldKey(fieldKey: string) {
  return fieldKey.split(':')[0]
}

function lineItemIdFromFieldKey(fieldKey: string) {
  return fieldKey.includes(':') ? fieldKey.split(':')[1] : undefined
}

function currentValueForIssue(record: StructuredDocumentRecord, fieldKey: string) {
  const lineItemId = lineItemIdFromFieldKey(fieldKey)
  const normalizedKey = baseFieldKey(fieldKey)

  if (lineItemId) {
    const lineItem = record.lineItems.find((item) => item.id === lineItemId)
    return lineItem?.fields[normalizedKey]?.displayValue || lineItem?.fields[normalizedKey]?.value || ''
  }

  return record.fields[normalizedKey]?.displayValue || record.fields[normalizedKey]?.value || ''
}

function lineItemLabelForIssue(record: StructuredDocumentRecord, fieldKey: string) {
  const lineItemId = lineItemIdFromFieldKey(fieldKey)
  if (!lineItemId) return ''
  const lineItem = record.lineItems.find((item) => item.id === lineItemId)
  if (!lineItem) return 'Line item'
  return lineItem.lineNumber ? `Line ${lineItem.lineNumber}` : 'Line item'
}

function severityRank(severity: CorrectiveActionItem['severity']) {
  switch (severity) {
    case 'blocks_acceptance':
      return 5
    case 'high':
      return 4
    case 'moderate':
      return 3
    case 'low':
      return 2
    case 'informational':
    default:
      return 1
  }
}
